<script setup lang="ts">
/**
 * Toast Notification Component - Laumeris
 */

import { computed, onMounted, ref } from 'vue'

interface Props {
  id: string
  type?: 'success' | 'error' | 'warning' | 'info'
  title?: string
  message: string
  duration?: number
  closable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: 'info',
  duration: 5000,
  closable: true
})

const emit = defineEmits<{
  close: [id: string]
}>()

const isVisible = ref(false)
const progress = ref(100)
let progressInterval: ReturnType<typeof setInterval> | null = null

const typeConfig = {
  success: {
    bg: 'bg-success-50 dark:bg-success-900/20',
    border: 'border-success-200 dark:border-success-800',
    icon: 'text-success-500',
    progress: 'bg-success-500'
  },
  error: {
    bg: 'bg-error-50 dark:bg-error-900/20',
    border: 'border-error-200 dark:border-error-800',
    icon: 'text-error-500',
    progress: 'bg-error-500'
  },
  warning: {
    bg: 'bg-warning-50 dark:bg-warning-900/20',
    border: 'border-warning-200 dark:border-warning-800',
    icon: 'text-warning-500',
    progress: 'bg-warning-500'
  },
  info: {
    bg: 'bg-primary-50 dark:bg-primary-900/20',
    border: 'border-primary-200 dark:border-primary-800',
    icon: 'text-primary-500',
    progress: 'bg-primary-500'
  }
}

const config = computed(() => typeConfig[props.type])

const close = () => {
  isVisible.value = false
  if (progressInterval) {
    clearInterval(progressInterval)
  }
  setTimeout(() => {
    emit('close', props.id)
  }, 300)
}

onMounted(() => {
  // Animate in
  setTimeout(() => {
    isVisible.value = true
  }, 10)

  // Auto-close with progress
  if (props.duration > 0) {
    const step = 100 / (props.duration / 100)
    progressInterval = setInterval(() => {
      progress.value -= step
      if (progress.value <= 0) {
        close()
      }
    }, 100)
  }
})
</script>

<template>
  <div
    :class="[
      'w-full max-w-sm overflow-hidden rounded-lg border shadow-lg transition-all duration-300 transform',
      config.bg,
      config.border,
      isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
    ]"
    role="alert"
  >
    <div class="p-4">
      <div class="flex items-start">
        <!-- Icon -->
        <div class="flex-shrink-0">
          <!-- Success -->
          <svg v-if="type === 'success'" :class="['w-5 h-5', config.icon]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <!-- Error -->
          <svg v-else-if="type === 'error'" :class="['w-5 h-5', config.icon]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <!-- Warning -->
          <svg v-else-if="type === 'warning'" :class="['w-5 h-5', config.icon]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <!-- Info -->
          <svg v-else :class="['w-5 h-5', config.icon]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>

        <!-- Content -->
        <div class="ml-3 flex-1">
          <p v-if="title" class="text-sm font-medium text-gray-900 dark:text-white">
            {{ title }}
          </p>
          <p :class="['text-sm', title ? 'mt-1 text-gray-600 dark:text-gray-300' : 'text-gray-900 dark:text-white']">
            {{ message }}
          </p>
        </div>

        <!-- Close Button -->
        <div v-if="closable" class="ml-4 flex-shrink-0">
          <button
            type="button"
            class="inline-flex rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            @click="close"
          >
            <span class="sr-only">Fermer</span>
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Progress Bar -->
    <div v-if="duration > 0" class="h-1 bg-gray-200 dark:bg-gray-700">
      <div
        :class="['h-full transition-all duration-100', config.progress]"
        :style="{ width: `${progress}%` }"
      />
    </div>
  </div>
</template>
